/**
 * Data Context
 * React context for accessing centralised application data
 */

import { createContext, useContext, useEffect, useState, useMemo, useRef, ReactNode } from 'react';
import { DataService } from '../services/DataService';
import { RealtimeService } from '../services/RealtimeService';
import type {
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
} from '../types/database';

interface DataContextValue {
  dataSync: { refreshing: boolean; error: string | null };
  initialLoadState: 'loading' | 'ready' | 'error';
  // Companies
  companies: Company[];
  getCompany: (id: string) => Company | undefined;
  createCompany: (company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => Company;
  updateCompany: (id: string, updates: Partial<Company>) => Company | undefined;
  deleteCompany: (id: string) => boolean;

  // Users
  users: User[];
  getUser: (id: string) => User | undefined;
  getUsersByCompany: (companyId: string) => User[];
  createUser: (user: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => User;
  updateUser: (id: string, updates: Partial<User>) => User | undefined;
  deleteUser: (id: string) => boolean;

  // Projects
  projects: Project[];
  getProject: (id: string) => Project | undefined;
  getProjectsByClient: (clientId: string) => Project[];
  getProjectsByStaff: (staffId: string) => Project[];
  createProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Project;
  updateProject: (id: string, updates: Partial<Project>) => Project | undefined;
  deleteProject: (id: string) => boolean;

  // Contract Items
  contractItems: ContractItem[];
  getContractItem: (id: string) => ContractItem | undefined;
  getContractItemsByProject: (projectId: string) => ContractItem[];
  getContractItemsByType: (projectId: string, type: string) => ContractItem[];
  createContractItem: (item: Omit<ContractItem, 'id' | 'createdAt' | 'updatedAt'>) => ContractItem;
  updateContractItem: (id: string, updates: Partial<ContractItem>) => ContractItem | undefined;
  deleteContractItem: (id: string) => boolean;

  // Interim Applications
  interimApplications: InterimApplication[];
  getInterimApplication: (id: string) => InterimApplication | undefined;
  getInterimApplicationsByProject: (projectId: string) => InterimApplication[];
  createInterimApplication: (app: Omit<InterimApplication, 'id' | 'createdAt' | 'updatedAt'>) => InterimApplication;
  updateInterimApplication: (id: string, updates: Partial<InterimApplication>) => InterimApplication | undefined;
  deleteInterimApplication: (id: string) => boolean;

  // Tenders
  tenders: Tender[];
  getTender: (id: string) => Tender | undefined;
  getTendersByClient: (clientId: string) => Tender[];
  getTendersByStatus: (status: string) => Tender[];
  createTender: (tender: Omit<Tender, 'id' | 'createdAt' | 'updatedAt'>) => Tender;
  updateTender: (id: string, updates: Partial<Tender>) => Tender | undefined;
  deleteTender: (id: string) => boolean;

  // Retention Ledgers
  retentionLedgers: RetentionLedger[];
  getRetentionLedger: (id: string) => RetentionLedger | undefined;
  getRetentionLedgerByProject: (projectId: string) => RetentionLedger | undefined;
  createRetentionLedger: (ledger: Omit<RetentionLedger, 'id' | 'createdAt' | 'updatedAt'>) => RetentionLedger;
  updateRetentionLedger: (id: string, updates: Partial<RetentionLedger>) => RetentionLedger | undefined;
  deleteRetentionLedger: (id: string) => boolean;

  // Tasks
  tasks: Task[];
  getTask: (id: string) => Task | undefined;
  getTasksByProject: (projectId: string) => Task[];
  getTasksByAssignee: (userId: string) => Task[];
  getTasksByStatus: (status: string) => Task[];
  createTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Task;
  updateTask: (id: string, updates: Partial<Task>) => Task | undefined;
  deleteTask: (id: string) => boolean;

  // Notifications
  notifications: Notification[];
  getNotification: (id: string) => Notification | undefined;
  getNotificationsByUser: (userId: string) => Notification[];
  getUnreadNotifications: (userId: string) => Notification[];
  createNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => Notification;
  markNotificationAsRead: (id: string) => Notification | undefined;
  markAllNotificationsAsRead: (userId: string) => void;
  deleteNotification: (id: string) => boolean;

  // Share Tokens
  shareTokens: ShareToken[];
  getShareToken: (id: string) => ShareToken | undefined;
  getShareTokenByToken: (token: string) => ShareToken | undefined;
  getShareTokensByProject: (projectId: string) => ShareToken[];
  createShareToken: (shareToken: Omit<ShareToken, 'id' | 'createdAt' | 'updatedAt'>) => ShareToken;
  updateShareToken: (id: string, updates: Partial<ShareToken>) => ShareToken | undefined;
  deleteShareToken: (id: string) => boolean;

  // Rate Libraries
  rateLibraries: RateLibrary[];
  getRateLibrary: (id: string) => RateLibrary | undefined;
  getRateLibrariesByCompany: (companyId: string) => RateLibrary[];
  createRateLibrary: (library: Omit<RateLibrary, 'id'>) => RateLibrary;
  updateRateLibrary: (id: string, updates: Partial<RateLibrary>) => RateLibrary | undefined;
  deleteRateLibrary: (id: string) => boolean;

  // Main Contractors
  mainContractors: MainContractor[];
  getMainContractor: (id: string) => MainContractor | undefined;
  createMainContractor: (contractor: Omit<MainContractor, 'id' | 'createdAt' | 'updatedAt'>) => MainContractor;
  updateMainContractor: (id: string, updates: Partial<MainContractor>) => MainContractor | undefined;
  deleteMainContractor: (id: string) => boolean;

  // Contacts (CRM)
  contacts: Contact[];
  getContact: (id: string) => Contact | undefined;
  getContactsByCompany: (companyId: string) => Contact[];
  getContactsByMainContractor: (mainContractorId: string) => Contact[];
  getContactsByType: (contactType: string) => Contact[];
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Contact;
  updateContact: (id: string, updates: Partial<Contact>) => Contact | undefined;
  deleteContact: (id: string) => boolean;

  // Activities
  activities: any[];
  getActivities: () => any[];
  getActivitiesByEntity: (entityType: string, entityId: string) => any[];
  getProjectActivities: (projectId: string) => any[];
  getClientActivities: (clientId: string) => any[];

  // Utilities
  getStats: () => any;
  exportDatabase: () => string;
  importDatabase: (jsonData: string) => void;
  resetDatabase: () => void;
  refresh: () => void;
}

const DataContext = createContext<DataContextValue | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Shared debounce timer — both DataService and Realtime events feed into the same
  // timer so a burst of simultaneous events only causes a single re-render.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleUpdate = () => {
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      setUpdateTrigger(prev => prev + 1);
    }, 120); // 120 ms window — batches any burst of writes/realtime events
  };

  // Subscribe to data changes from DataService
  useEffect(() => {
    const unsubscribe = DataService.subscribe(scheduleUpdate);
    return unsubscribe;
  }, []);

  // Subscribe to realtime database changes
  useEffect(() => {
    // Subscribe to all critical tables
    const tables = [
      'projects',
      'companies',
      'users',
      'contract_items',
      'interim_applications',
      'retention_ledgers',
      'tasks',
      'notifications',
      'share_tokens',
      'rate_libraries',
      'main_contractors',
      'contacts',
      'activities',
    ] as const;

    const unsubscribe = RealtimeService.subscribeMultiple(
      tables.map(table => ({
        table,
        // All table events share the same debounced timer — a Realtime burst
        // (e.g. 6 tasks changing at once) triggers exactly one re-render.
        //
        // The event also has to REFETCH. A postgres_changes payload tells us a
        // row moved, but the app reads from DataService's local cache, and
        // nothing here was updating it — so a change made on another device
        // bumped the render trigger and every consumer re-read the same stale
        // rows. refreshFromBackend is throttled internally, so a burst still
        // costs one fetch.
        onChange: () => {
          void DataService.refreshFromBackend().catch(() => { /* stale beats a crash */ });
          scheduleUpdate();
        },
      }))
    );

    return () => {
      unsubscribe();
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Force refresh (immediate — used by manual refresh buttons)
  const refresh = () => {
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    setUpdateTrigger(prev => prev + 1);
  };

  // Memoize the value object to prevent unnecessary re-renders
  // Only recompute when updateTrigger changes (which happens when data changes)
  const value: DataContextValue = useMemo(() => ({
    initialLoadState: DataService.getInitialLoadState(),
    dataSync: DataService.getDataSyncState(),
    // Companies
    companies: DataService.getCompanies(),
    getCompany: DataService.getCompany.bind(DataService),
    createCompany: DataService.createCompany.bind(DataService),
    updateCompany: DataService.updateCompany.bind(DataService),
    deleteCompany: DataService.deleteCompany.bind(DataService),

    // Users
    users: DataService.getUsers(),
    getUser: DataService.getUser.bind(DataService),
    getUsersByCompany: DataService.getUsersByCompany.bind(DataService),
    createUser: DataService.createUser.bind(DataService),
    updateUser: DataService.updateUser.bind(DataService),
    deleteUser: DataService.deleteUser.bind(DataService),

    // Projects
    projects: DataService.getProjects(),
    getProject: DataService.getProject.bind(DataService),
    getProjectsByClient: DataService.getProjectsByClient.bind(DataService),
    getProjectsByStaff: DataService.getProjectsByStaff.bind(DataService),
    createProject: DataService.createProject.bind(DataService),
    updateProject: DataService.updateProject.bind(DataService),
    deleteProject: DataService.deleteProject.bind(DataService),

    // Contract Items
    contractItems: DataService.getContractItems(),
    getContractItem: DataService.getContractItem.bind(DataService),
    getContractItemsByProject: DataService.getContractItemsByProject.bind(DataService),
    getContractItemsByType: DataService.getContractItemsByType.bind(DataService),
    createContractItem: DataService.createContractItem.bind(DataService),
    updateContractItem: DataService.updateContractItem.bind(DataService),
    deleteContractItem: DataService.deleteContractItem.bind(DataService),

    // Interim Applications
    interimApplications: DataService.getInterimApplications(),
    getInterimApplication: DataService.getInterimApplication.bind(DataService),
    getInterimApplicationsByProject: DataService.getInterimApplicationsByProject.bind(DataService),
    createInterimApplication: DataService.createInterimApplication.bind(DataService),
    updateInterimApplication: DataService.updateInterimApplication.bind(DataService),
    deleteInterimApplication: DataService.deleteInterimApplication.bind(DataService),

    // Tenders
    tenders: DataService.getTenders(),
    getTender: DataService.getTender.bind(DataService),
    getTendersByClient: DataService.getTendersByClient.bind(DataService),
    getTendersByStatus: DataService.getTendersByStatus.bind(DataService),
    createTender: DataService.createTender.bind(DataService),
    updateTender: DataService.updateTender.bind(DataService),
    deleteTender: DataService.deleteTender.bind(DataService),

    // Retention Ledgers
    retentionLedgers: DataService.getRetentionLedgers(),
    getRetentionLedger: DataService.getRetentionLedger.bind(DataService),
    getRetentionLedgerByProject: DataService.getRetentionLedgerByProject.bind(DataService),
    createRetentionLedger: DataService.createRetentionLedger.bind(DataService),
    updateRetentionLedger: DataService.updateRetentionLedger.bind(DataService),
    deleteRetentionLedger: DataService.deleteRetentionLedger.bind(DataService),

    // Tasks
    tasks: DataService.getTasks(),
    getTask: DataService.getTask.bind(DataService),
    getTasksByProject: DataService.getTasksByProject.bind(DataService),
    getTasksByAssignee: DataService.getTasksByAssignee.bind(DataService),
    getTasksByStatus: DataService.getTasksByStatus.bind(DataService),
    createTask: DataService.createTask.bind(DataService),
    updateTask: DataService.updateTask.bind(DataService),
    deleteTask: DataService.deleteTask.bind(DataService),

    // Notifications
    notifications: DataService.getNotifications(),
    getNotification: DataService.getNotification.bind(DataService),
    getNotificationsByUser: DataService.getNotificationsByUser.bind(DataService),
    getUnreadNotifications: DataService.getUnreadNotifications.bind(DataService),
    createNotification: DataService.createNotification.bind(DataService),
    markNotificationAsRead: DataService.markNotificationAsRead.bind(DataService),
    markAllNotificationsAsRead: DataService.markAllNotificationsAsRead.bind(DataService),
    deleteNotification: DataService.deleteNotification.bind(DataService),

    // Share Tokens
    shareTokens: DataService.getShareTokens(),
    getShareToken: DataService.getShareToken.bind(DataService),
    getShareTokenByToken: DataService.getShareTokenByToken.bind(DataService),
    getShareTokensByProject: DataService.getShareTokensByProject.bind(DataService),
    createShareToken: DataService.createShareToken.bind(DataService),
    updateShareToken: DataService.updateShareToken.bind(DataService),
    deleteShareToken: DataService.deleteShareToken.bind(DataService),

    // Rate Libraries
    rateLibraries: DataService.getRateLibraries(),
    getRateLibrary: DataService.getRateLibrary.bind(DataService),
    getRateLibrariesByCompany: DataService.getRateLibrariesByCompany.bind(DataService),
    createRateLibrary: DataService.createRateLibrary.bind(DataService),
    updateRateLibrary: DataService.updateRateLibrary.bind(DataService),
    deleteRateLibrary: DataService.deleteRateLibrary.bind(DataService),

    // Main Contractors
    mainContractors: DataService.getMainContractors(),
    getMainContractor: DataService.getMainContractor.bind(DataService),
    createMainContractor: DataService.createMainContractor.bind(DataService),
    updateMainContractor: DataService.updateMainContractor.bind(DataService),
    deleteMainContractor: DataService.deleteMainContractor.bind(DataService),

    // Contacts (CRM)
    contacts: DataService.getContacts(),
    getContact: DataService.getContact.bind(DataService),
    getContactsByCompany: DataService.getContactsByCompany.bind(DataService),
    getContactsByMainContractor: DataService.getContactsByMainContractor.bind(DataService),
    getContactsByType: DataService.getContactsByType.bind(DataService),
    createContact: DataService.createContact.bind(DataService),
    updateContact: DataService.updateContact.bind(DataService),
    deleteContact: DataService.deleteContact.bind(DataService),

    // Activities
    activities: DataService.getActivities(),
    getActivities: DataService.getActivities.bind(DataService),
    getActivitiesByEntity: DataService.getActivitiesByEntity.bind(DataService),
    getProjectActivities: DataService.getProjectActivities.bind(DataService),
    getClientActivities: DataService.getClientActivities.bind(DataService),

    // Utilities
    getStats: DataService.getStats.bind(DataService),
    exportDatabase: DataService.exportDatabase.bind(DataService),
    importDatabase: DataService.importDatabase.bind(DataService),
    resetDatabase: DataService.resetDatabase.bind(DataService),
    refresh,
  }), [updateTrigger]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}